import { compactGeometry } from "@project/emerge/src/util/compactGeometry";
import {
  applyCreasedNormals,
  PART_CREASE_ANGLE_DEG,
} from "@project/emerge/src/util/creasedNormals";
import {
  BufferAttribute,
  BufferGeometry,
  Matrix4,
  type Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from "three";
import { MiterType } from "../enum/MiterType";

/**
 * U coordinate for each point of the profile cross-section, plus the width it
 * was measured over.
 *
 * Arc length along the section, normalised, rather than the projection onto Y
 * this used to be. A projection gives every point at the same height the same
 * U, so any face of the section that runs horizontally — the top of an L, the
 * flanges of a channel — collapses to a single column of the texture: no
 * variation across it at all, and a stretched sample everywhere else. Arc
 * length walks the section as it is actually drawn, so a millimetre of profile
 * is a millimetre of texture wherever on the section it sits, which is what
 * lets a die-line pattern keep one physical spacing all the way round.
 *
 * The width is returned with it because a normalised U alone cannot say how
 * many millimetres it spans, and a material sizing a physical pattern has to
 * know. See `getProfileAluminumMaterial`.
 *
 * The walk closes: the section is a closed polygon — the geometry wraps the
 * last profile point back to the first — so the edge between them is a face
 * like any other and its length belongs in the developed width. Leaving it out
 * both understates the width the material tiles against and puts U = 1 on a
 * point that is not U = 0's, which is exactly what the periodic tile assumes.
 *
 * The side mesh appends a copy of the first vertex at U = 1. This measured
 * ring stays unseamed: neither its perimeter nor the cap triangulation should
 * acquire an extra point just because the texture needs two values there.
 */
function developedSectionUvs(profile: BufferAttribute): {
  u: number[];
  width: number;
  ring: Vector2[];
} {
  // Read back out of the geometry rather than from `profileShape.extractPoints`,
  // which is the same list only some of the time: ShapeGeometry reverses the
  // points when the shape is wound clockwise, and the profiles are built with
  // either winding depending on which side of the case they sit on. A U taken
  // from the shape order would then be attached to the vertices in reverse for
  // half of them — not a mirrored pattern, which would be harmless, but arc
  // lengths paired with the wrong vertices, which puts a jump at every corner.
  //
  // The section lies in XZ here: the geometry has already been rotated onto its
  // extrusion axis.
  const ring: Vector2[] = [];
  for (let i = 0; i < profile.count; i++) {
    ring.push(new Vector2(profile.getX(i), profile.getZ(i)));
  }

  const distances: number[] = [0];
  let total = 0;
  for (let i = 1; i < ring.length; i++) {
    total += (ring[i] as Vector2).distanceTo(ring[i - 1] as Vector2);
    distances.push(total);
  }
  if (ring.length > 1) {
    total += (ring.at(-1) as Vector2).distanceTo(ring[0] as Vector2);
  }
  // A degenerate section (one point, or all points coincident) would divide by
  // zero; it has no surface to texture either way.
  const width = total > 0 ? total : 1;
  return { u: distances.map((distance) => distance / width), width, ring };
}

type EndProfile = {
  positions: BufferAttribute;
  uvs: number[];
};

/** Isotropic planar UVs in the actual cut plane, including a miter's shear. */
function planarEndUvs(profile: BufferAttribute, placement: Matrix4): number[] {
  if (profile.count === 0) return [];
  const uAxis = new Vector3(1, 0, 0).transformDirection(placement);
  const vAxis = new Vector3(0, 0, 1).transformDirection(placement);
  vAxis.addScaledVector(uAxis, -vAxis.dot(uAxis)).normalize();

  const origin = new Vector3().fromBufferAttribute(profile, 0);
  const point = new Vector3();
  const uvs: number[] = [];
  let minU = Number.POSITIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxU = Number.NEGATIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < profile.count; i++) {
    point.fromBufferAttribute(profile, i).sub(origin);
    const u = point.dot(uAxis);
    const v = point.dot(vAxis);
    uvs.push(u, v);
    minU = Math.min(minU, u);
    minV = Math.min(minV, v);
    maxU = Math.max(maxU, u);
    maxV = Math.max(maxV, v);
  }
  // One scale for both axes avoids stretching a thin cut end into a square.
  const span = Math.max(maxU - minU, maxV - minV) || 1;
  return uvs.map((value, i) => (value - (i % 2 === 0 ? minU : minV)) / span);
}

export class ProfiledContourGeometry extends BufferGeometry {
  /**
   * Developed width of the cross-section in millimetres — the distance walked
   * right round the closed section, not its bounding box. Read by the material
   * to size a pattern in physical units against the normalised U above.
   */
  readonly sectionWidth: number;

  /**
   * The ring the U above was measured along, in the section's own XY, and the
   * U at each of its points — kept so that {@link sectionU} can answer where
   * on the texture a given point of the cross-section lands.
   */
  private readonly ring: readonly Vector2[];
  private readonly ringU: readonly number[];

  /**
   * Developed U of the point on the cross-section closest to `point`, given in
   * the same XY the profile shape was built in (before the geometry rotated it
   * onto the extrusion axis).
   *
   * This is how a material places a feature at a physical spot on the section
   * — a groove down the middle of the outer face, say — without knowing how
   * `ShapeGeometry` ordered the ring or how the corners were rounded: it names
   * the spot, and gets back the U the geometry actually interpolates there.
   */
  sectionU(point: Vector2): number {
    const { ring, ringU } = this;
    if (ring.length === 0) return 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    let bestU = 0;
    const closest = new Vector2();
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i] as Vector2;
      const b = ring[(i + 1) % ring.length] as Vector2;
      const ab = b.clone().sub(a);
      const lengthSquared = ab.lengthSq();
      const t =
        lengthSquared === 0
          ? 0
          : Math.min(
              1,
              Math.max(0, point.clone().sub(a).dot(ab) / lengthSquared),
            );
      closest.copy(a).addScaledVector(ab, t);
      const distance = closest.distanceToSquared(point);
      if (distance >= bestDistance) continue;
      bestDistance = distance;
      const uA = ringU[i] ?? 0;
      // Closing-edge interpolation ends at the side's duplicate U = 1. The
      // exact seam can still return U = 0, its periodic texture equivalent.
      const uB = i === ring.length - 1 ? 1 : (ringU[i + 1] ?? 0);
      bestU = uA + (uB - uA) * t;
    }
    return bestU;
  }

  constructor(
    profileShape: Shape,
    contour: Vector2[],
    miterType: MiterType = MiterType.NONE,
    ccw = true,
  ) {
    super();
    const shapeBufferGeometry = new ShapeGeometry(profileShape);
    try {
      shapeBufferGeometry.rotateX(Math.PI * 0.5);
      const profileAttr = shapeBufferGeometry.getAttribute("position");
      if (!(profileAttr instanceof BufferAttribute)) {
        throw new TypeError(
          "ProfiledContourGeometry: expected profile position attribute",
        );
      }
      const profile = profileAttr;

      // Only side rings have a UV seam. Caps retain ShapeGeometry's original
      // vertex count and indices, after all of the expanded side rings.
      const sideProfileCount = profile.count + 1;
      const profilePoints = new Float32Array(
        (sideProfileCount * contour.length + profile.count * 2) * 3,
      );

      const endProfiles: EndProfile[] = [];
      const texCoords: number[] = [];
      const section = developedSectionUvs(profile);
      this.sectionWidth = section.width;
      this.ring = section.ring;
      this.ringU = section.u;

      this.processContourPoints({
        contour,
        profile,
        profilePoints,
        endProfiles,
        texCoords,
        sectionU: section.u,
        miterType,
        ccw,
      });

      this.processEndProfiles(
        endProfiles,
        profilePoints,
        texCoords,
        sideProfileCount * contour.length,
      );

      const originalPositions = new BufferAttribute(profilePoints, 3);
      const originalUvs = new BufferAttribute(new Float32Array(texCoords), 2);

      const index = this.generateIndices(
        contour,
        profile.count,
        shapeBufferGeometry,
      );

      this.applyCreasedShading(index, originalPositions, originalUvs);
      // Index exact tuples only after the final normals and UVs are attached.
      // Keep this instance, its section metadata, creases and UV seams intact.
      compactGeometry(this);
    } finally {
      // Only copied positions reach this geometry; the shape owns its buffers.
      shapeBufferGeometry.dispose();
    }
  }

  private processContourPoints(options: {
    contour: Vector2[];
    profile: BufferAttribute;
    profilePoints: Float32Array;
    endProfiles: EndProfile[];
    texCoords: number[];
    sectionU: number[];
    miterType: MiterType;
    ccw: boolean;
  }): void {
    const {
      contour,
      profile,
      profilePoints,
      endProfiles,
      texCoords,
      sectionU,
      miterType,
      ccw,
    } = options;
    for (let i = 0; i < contour.length; i++) {
      const { v1, v2 } = this.calculateVectors(contour, i);
      const { hA, tA } = this.calculateAngles(v1, v2, i, contour.length);
      const matrices = this.buildMatrices({
        hA,
        tA,
        v1,
        v2,
        contourPoint: contour[i],
        index: i,
        contourLength: contour.length,
        miterType,
        ccw,
      });

      // One composed transform rather than four sweeps over the section: the
      // matrices are built fresh per contour point, so multiplying them here
      // is free and the profile's positions are read and written once.
      const placement = matrices.translation
        .multiply(matrices.rotation2)
        .multiply(matrices.rotation)
        .multiply(matrices.shift);

      const cloneProfile = profile.clone();
      cloneProfile.applyMatrix4(placement);

      this.addTextureCoords(sectionU, texCoords, i, contour.length);
      const offset = (profile.count + 1) * i * 3;
      profilePoints.set(cloneProfile.array, offset);
      // Copy the already transformed vertex bit-for-bit so position welding
      // still joins the two sides of a smooth seam when computing normals.
      profilePoints.set(
        cloneProfile.array.subarray(0, 3),
        offset + profile.count * 3,
      );

      if (i === 0 || i === contour.length - 1) {
        endProfiles.push({
          positions: cloneProfile,
          uvs: planarEndUvs(cloneProfile, placement),
        });
      }
    }
  }

  private calculateVectors(
    contour: Vector2[],
    i: number,
  ): { v1: Vector2; v2: Vector2 } {
    const prevIdx = i - 1 < 0 ? contour.length - 1 : i - 1;
    const nextIdx = i + 1 === contour.length ? 0 : i + 1;
    const v1 = new Vector2().subVectors(
      contour[prevIdx] ?? new Vector2(0, 0),
      contour[i] ?? new Vector2(0, 0),
    );
    const v2 = new Vector2().subVectors(
      contour[nextIdx] ?? new Vector2(0, 0),
      contour[i] ?? new Vector2(0, 0),
    );
    return { v1, v2 };
  }

  private calculateAngles(
    v1: Vector2,
    v2: Vector2,
    i: number,
    contourLength: number,
  ): { hA: number; tA: number } {
    const angle = v2.angle() - v1.angle();
    const halfAngle = angle * 0.5;

    let hA = halfAngle;
    let tA = v2.angle() + Math.PI * 0.5;

    if (i === 0 || i === contourLength - 1) {
      hA = Math.PI * 0.5;
    }
    if (i === contourLength - 1) {
      tA = v1.angle() - Math.PI * 0.5;
    }

    return { hA, tA };
  }

  private buildMatrices(options: {
    hA: number;
    tA: number;
    v1: Vector2;
    v2: Vector2;
    contourPoint: Vector2 | undefined;
    index: number;
    contourLength: number;
    miterType: MiterType;
    ccw: boolean;
  }): {
    shift: Matrix4;
    rotation: Matrix4;
    rotation2: Matrix4;
    translation: Matrix4;
  } {
    const {
      hA,
      tA,
      v1,
      v2,
      contourPoint,
      index,
      contourLength,
      miterType,
      ccw,
    } = options;
    const shift = Math.tan(hA - Math.PI * 0.5);
    const shiftMatrix = new Matrix4().set(
      1,
      0,
      0,
      0,
      -shift,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
    );

    const rotationMatrix = new Matrix4().set(
      Math.cos(tA),
      -Math.sin(tA),
      0,
      0,
      Math.sin(tA),
      Math.cos(tA),
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
    );

    const rotationMatrix2 = this.buildMiterRotationMatrix({
      index,
      contourLength,
      v1,
      v2,
      miterType,
      ccw,
    });

    const translationMatrix = new Matrix4().set(
      1,
      0,
      0,
      contourPoint?.x ?? 0,
      0,
      1,
      0,
      contourPoint?.y ?? 0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
    );

    return {
      shift: shiftMatrix,
      rotation: rotationMatrix,
      rotation2: rotationMatrix2,
      translation: translationMatrix,
    };
  }

  private buildMiterRotationMatrix(options: {
    index: number;
    contourLength: number;
    v1: Vector2;
    v2: Vector2;
    miterType: MiterType;
    ccw: boolean;
  }): Matrix4 {
    const { index, contourLength, v1, v2, miterType, ccw } = options;
    const rotationMatrix2 = new Matrix4().identity();

    const isStartMiter = this.isStartMiter(index, miterType, ccw);
    const isEndMiter = this.isEndMiter(index, contourLength, miterType, ccw);

    if (isStartMiter) {
      this.applyMiterRotation(rotationMatrix2, v2, ccw);
    }

    if (isEndMiter) {
      this.applyMiterRotation(rotationMatrix2, v1, ccw);
    }

    return rotationMatrix2;
  }

  private isStartMiter(
    index: number,
    miterType: MiterType,
    ccw: boolean,
  ): boolean {
    const miterAtStart = ccw ? MiterType.START : MiterType.END;
    return (
      index === 0 &&
      (miterType === MiterType.BOTH || miterType === miterAtStart)
    );
  }

  private isEndMiter(
    index: number,
    contourLength: number,
    miterType: MiterType,
    ccw: boolean,
  ): boolean {
    const miterAtEnd = ccw ? MiterType.END : MiterType.START;
    return (
      index === contourLength - 1 &&
      (miterType === MiterType.BOTH || miterType === miterAtEnd)
    );
  }

  private applyMiterRotation(
    matrix: Matrix4,
    vector: Vector2,
    ccw: boolean,
  ): void {
    // Both endpoint vectors point into the run. Shear along that direction:
    // selecting only X on a diagonal also displaces the section transversely,
    // twisting the side quads so their triangles can fold through each other.
    // Unit direction retains the existing +/-1 shear on axis-aligned runs.
    const direction = vector
      .clone()
      .normalize()
      .multiplyScalar(ccw ? -1 : 1);
    matrix.elements[8] = direction.x;
    matrix.elements[9] = direction.y;
  }

  private addTextureCoords(
    sectionU: number[],
    texCoords: number[],
    i: number,
    contourLength: number,
  ): void {
    for (const u of sectionU) {
      texCoords.push(u);
      texCoords.push(i / contourLength);
    }
    texCoords.push(1, i / contourLength);
  }

  private processEndProfiles(
    endProfiles: EndProfile[],
    profilePoints: Float32Array,
    texCoords: number[],
    sideVertexCount: number,
  ): void {
    for (let idx = 0; idx < endProfiles.length; idx++) {
      const ep = endProfiles[idx];
      if (!ep) continue;
      texCoords.push(...ep.uvs);
      profilePoints.set(
        ep.positions.array,
        (sideVertexCount + ep.positions.count * idx) * 3,
      );
    }
  }

  private generateIndices(
    contour: Vector2[],
    profileCount: number,
    shapeBufferGeometry: ShapeGeometry,
  ): number[] {
    const index: number[] = [];
    const lastCorner = contour.length - 1;
    const sideProfileCount = profileCount + 1;

    for (let i = 0; i < lastCorner; i++) {
      for (let j = 0; j < profileCount; j++) {
        const currCorner = i;
        const nextCorner = i + 1;
        const currPoint = j;
        const nextPoint = j + 1;

        const a = nextPoint + sideProfileCount * currCorner;
        const b = currPoint + sideProfileCount * currCorner;
        const c = currPoint + sideProfileCount * nextCorner;
        const d = nextPoint + sideProfileCount * nextCorner;

        index.push(a, b, d, b, c, d);
      }
    }

    const shapeIdx = shapeBufferGeometry.getIndex();
    if (!shapeIdx) {
      throw new Error(
        "ProfiledContourGeometry: expected index buffer on profile geometry",
      );
    }
    const capOffset = sideProfileCount * contour.length;
    // The start cap only needs reversed winding, not mirrored scratch
    // positions or recomputed normals. Preserve each triangle's a, c, b order.
    for (let i = 0; i < shapeIdx.count; i += 3) {
      index.push(
        shapeIdx.getX(i) + capOffset,
        shapeIdx.getX(i + 2) + capOffset,
        shapeIdx.getX(i + 1) + capOffset,
      );
    }
    for (const i of shapeIdx.array) index.push(i + capOffset + profileCount);

    return index;
  }

  /**
   * Shades the profile with creased normals instead of one normal per face.
   *
   * The extrusions are the shiniest parts of a case, so the facets of a
   * flat-shaded fillet are the most visible "this is CAD" tell in the picture.
   * Angles above {@link PART_CREASE_ANGLE_DEG} — the real 90° edges of the
   * section, and the joints between path segments — stay sharp; the polygonal
   * approximation of a rounded corner is smoothed into the curve it stands for.
   */
  private applyCreasedShading(
    index: number[],
    originalPositions: BufferAttribute,
    originalUvs: BufferAttribute,
  ): void {
    const source = new BufferGeometry();
    source.setAttribute("position", originalPositions);
    source.setAttribute("uv", originalUvs);
    source.setIndex(index);

    const creased = applyCreasedNormals(source);

    this.setIndex(creased.getIndex());
    creased.setIndex(null);
    for (const name of ["position", "normal", "uv"] as const) {
      const attribute = creased.getAttribute(name);
      if (attribute) {
        this.setAttribute(name, attribute);
        // Detached before the dispose below. `dispose()` walks the geometry's
        // attributes and frees the GL buffer keyed by each one, and these
        // objects now belong to `this` — harmless while nothing has uploaded
        // them, and a mid-frame buffer deletion the moment anything has.
        creased.deleteAttribute(name);
      }
    }
    creased.dispose();
    if (source !== creased) {
      // `source` was handed the caller's own attributes; the same applies.
      source.deleteAttribute("position");
      source.deleteAttribute("uv");
      source.dispose();
    }
  }
}
